import type { CardItem } from "@/interfaces/CardItem";
import type { DeckCardsMap } from "@/interfaces/DeckIdentity";
import type { Item } from "@/components/Filters/GenericFilter";

/**
 * A game's deck builder filters, declared rather than hand-written.
 *
 * One Piece, Union Arena and Riftbound each ship three parallel files: a
 * component laying out the filter bar, a getDefaultFilters, and a filterCards
 * walking the same fields a third time. The three drift -- a filter added to the
 * bar does nothing until the matching branch is added to filterCards.
 *
 * A spec is the single declaration those three are derived from. A game says
 * which fields it filters on, and gets the bar, the defaults and the filtering
 * from it. Adding a filter to a game is a line in its spec; adding a game is a
 * spec, with no new components.
 */

/** A dropdown's options, and the card values each one matches. */
export interface FilterOption {
  /** Stored in filter state and in the url. */
  key: string;
  /** Shown in the dropdown. */
  label: string;
  /** Tailwind background class for a swatch, as the colour filters use. */
  swatch?: string;
}

interface BaseFilterSpec {
  /** Key in the filter state object. */
  key: string;
  /** Placeholder shown on the control. */
  label: string;
}

/**
 * A multi-select dropdown over one card field. This covers nearly every deck
 * builder filter: colour, cost, power, type, rarity, set.
 */
export interface SelectFilterSpec extends BaseFilterSpec {
  kind: "select";
  /**
   * The card's value(s) for this field. Returning several means the card
   * matches if any of them is selected, which is what a list-valued field like
   * Palworld's Pal types or work aptitudes needs.
   */
  valuesOf: (card: CardItem) => (string | number | null | undefined)[];
  /**
   * The options to offer. Omit to derive them from the cards in the pool, which
   * is what a field whose values are open-ended (sets, costs) wants -- a new set
   * then appears in the filter with no code change.
   */
  options?: FilterOption[];
  /** Orders derived options. Defaults to numeric-aware ascending. */
  compareOptions?: (a: string, b: string) => number;
  /** Tailwind width for the control. */
  width?: string;
  selectionMode?: "single" | "multiple";
}

/**
 * The search box and its "search in" dropdown. A game lists the fields its
 * search can look in; "all" searches every one of them.
 */
export interface SearchFilterSpec {
  kind: "search";
  /** Key per searchable field, plus the text it reads off a card. */
  fields: { key: string; label: string; textOf: (card: CardItem) => string }[];
}

/** The "Show AAs" switch, for games with alternate-art printings. */
export interface ToggleFilterSpec extends BaseFilterSpec {
  kind: "toggle";
  defaultValue?: boolean;
}

export type FilterSpec = SelectFilterSpec | SearchFilterSpec | ToggleFilterSpec;

/** A game's whole filter bar, in the order it is laid out. */
export type DeckBuilderFilterSpec = FilterSpec[];

/** Filter state: a Set per dropdown, plus the search text and the toggles. */
export type FilterState = Record<string, any>;

/** Numbers sort numerically, everything else alphabetically. */
function compareValues(a: string, b: string): number {
  const na = Number(a);
  const nb = Number(b);

  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na - nb;
  return a.localeCompare(b);
}

/** A card's values for a select filter, as the strings the options use. */
function selectedValuesOf(spec: SelectFilterSpec, card: CardItem): string[] {
  return spec
    .valuesOf(card)
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map(String);
}

/**
 * The options a dropdown offers: what the game declared, or every value present
 * in the pool when it declared none.
 */
export function optionsFor(
  spec: SelectFilterSpec,
  cards: CardItem[],
): FilterOption[] {
  if (spec.options) return spec.options;

  const seen = new Set<string>();
  for (const card of cards) {
    for (const value of selectedValuesOf(spec, card)) seen.add(value);
  }

  return [...seen]
    .sort(spec.compareOptions ?? compareValues)
    .map((value) => ({ key: value, label: value }));
}

/**
 * The filter state a game starts in: nothing selected, matching every card.
 *
 * The deck builder compares live filters against this to decide whether to skip
 * filtering entirely, so it has to be value-equal to a freshly cleared state.
 */
export function getDefaultFiltersFromSpec(
  spec: DeckBuilderFilterSpec,
): FilterState {
  const filters: FilterState = {};

  for (const field of spec) {
    if (field.kind === "search") {
      filters.search = "";
      filters.searchSelect = new Set<string>(["all"]);
    } else if (field.kind === "toggle") {
      filters[field.key] = field.defaultValue ?? false;
    } else {
      filters[field.key] = new Set<string>();
    }
  }

  return filters;
}

/** Whether a card passes one select filter. Unselected filters pass everything. */
function passesSelect(
  spec: SelectFilterSpec,
  card: CardItem,
  selected: Set<string>,
): boolean {
  if (!selected || selected.size === 0) return true;

  // A card with several values for the field matches if any of them is picked.
  return selectedValuesOf(spec, card).some((value) => selected.has(value));
}

/** Whether a card passes the search box. */
function passesSearch(
  spec: SearchFilterSpec,
  card: CardItem,
  filters: FilterState,
): boolean {
  const search = (filters.search ?? "").trim().toLowerCase();
  if (!search) return true;

  const chosen: string | undefined = Array.from(
    filters.searchSelect ?? new Set(),
  )[0] as string | undefined;

  const fields =
    !chosen || chosen === "all"
      ? spec.fields
      : spec.fields.filter((field) => field.key === chosen);

  return fields.some((field) =>
    field.textOf(card).toLowerCase().includes(search),
  );
}

/**
 * Filter a card pool against a spec.
 *
 * Signature matches GameHandler.filterCards, so a game's handler can hand this
 * straight through.
 */
export function filterCardsFromSpec(
  spec: DeckBuilderFilterSpec,
  cards: CardItem[],
  filters: FilterState,
  _defaultFilters: FilterState,
  _deckCardsMap: DeckCardsMap,
): CardItem[] {
  return cards.filter((card) =>
    spec.every((field) => {
      if (field.kind === "search") return passesSearch(field, card, filters);
      // A toggle changes which printings are offered, not which cards match.
      if (field.kind === "toggle") return true;
      return passesSelect(field, card, filters[field.key]);
    }),
  );
}

/** The "search in" dropdown's options, in the shape GenericFilter wants. */
export function searchSelectItems(spec: SearchFilterSpec): Item[] {
  return [
    { key: "all", value: "All" },
    ...spec.fields.map((field) => ({ key: field.key, value: field.label })),
  ];
}
